import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from 'src/app/services/api/api.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ApprovalService } from 'src/app/services/approval/approval.service';
import { ToastService } from 'src/app/services/toast/toast.service';

@Component({
  selector: 'app-dashboard-approval-draft',
  templateUrl: './dashboard-approval-draft.component.html',
  styleUrls: ['./dashboard-approval-draft.component.scss'],
})
export class DashboardApprovalDraftComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() comparingVersions: boolean = false;
  loading = true;

  form = this.formBuilder.group({
    content: [null, [Validators.required]],
    fund: [null, [Validators.required]],
  });

  role: string = '';
  userId: number | null = null;
  haveCoobligation: boolean = false;
  selectedDraft = null;

  // Dados para versão principal
  mainData: Partial<any>[] = [];
  mainLoading = false;

  // Dados para versão comparando
  comparingData: Partial<any>[] = [];
  comparingLoading = false;
  comparingDataLoading = false;

  // Dados normais (sem comparação)
  data: Partial<any>[] = [];

  funds: SelectItem[] = [];
  document: any;

  documentation = [];

  eventMainVersion: any;
  eventComparingVersion: any;

  showModal = false;

  canView = false;
  canAdd = false;
  canChange = false;
  canRemove = false;

  quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link'],
      ['clean']
    ]
  };

  constructor(
    private formBuilder: FormBuilder,
    private api: ApiService,
    private toast: ToastService,
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    public approvalService: ApprovalService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    let range = this.router.url.split('/').length;
    this.role = this.router.url.split('/')[range - 2];

    this.canView = this.authService.verifyPermission(
      `de_can_view_${this.router.url.split('/')[range - 2]}_draft_details`
    );
    this.canAdd = this.authService.verifyPermission(
      `de_can_view_${this.router.url.split('/')[range - 2]}_draft_add`
    );
    this.canChange = this.authService.verifyPermission(
      `de_can_view_${this.router.url.split('/')[range - 2]}_draft_change`
    );
    this.canRemove = this.authService.verifyPermission(
      `de_can_view_${this.router.url.split('/')[range - 2]}_draft_remove`
    );

    if (this.comparingVersions) {
      this.loadComparingVersions();
    } else {
      // Não chamar APIs v2 - sem dados no cache, não fazer loading
      this.data = [];
      this.loading = false;
    }

    this.eventMainVersion = this.approvalService.changeMainVersion.subscribe(
      () => {
        if (this.comparingVersions) {
          this.loadMainVersion();
        }
      }
    );

    this.eventComparingVersion =
      this.approvalService.changeComparingVersion.subscribe(() => {
        if (this.comparingVersions) {
          this.loadComparingVersion();
        }
      });
  }

  ngOnDestroy(): void {
    if (this.eventMainVersion) {
      this.eventMainVersion.unsubscribe();
    }
    if (this.eventComparingVersion) {
      this.eventComparingVersion.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // Detectar quando comparingVersions muda de true para false
    if (
      changes['comparingVersions'] &&
      !changes['comparingVersions'].firstChange &&
      changes['comparingVersions'].previousValue === true &&
      changes['comparingVersions'].currentValue === false
    ) {
      // Recarregar dados para visualização normal
      this.getApprovalData();
    }
  }

  loadComparingVersions() {
    // Limpar dados anteriores
    this.mainData = [];
    this.comparingData = [];
    this.mainLoading = false;
    this.comparingLoading = false;

    // Só carregar se houver versões selecionadas
    if (this.approvalService.approvalData.mainVersion) {
      this.loadMainVersion();
    }
    if (this.approvalService.approvalData.comparingVersion) {
      this.loadComparingVersion();
    }
  }

  loadMainVersion() {
    if (!this.approvalService.approvalData.mainVersion) {
      this.mainData = [];
      this.mainLoading = false;
      return;
    }
    this.mainLoading = true;
    this.cdr.detectChanges();
    this.getApprovalData('main');
  }

  loadComparingVersion() {
    if (!this.approvalService.approvalData.comparingVersion) {
      this.comparingData = [];
      this.comparingLoading = false;
      this.comparingDataLoading = false;
      return;
    }
    this.comparingLoading = true;
    this.comparingDataLoading = true;
    this.cdr.detectChanges();
    this.getApprovalData('comparing').then(() => {
      // Após carregar os dados, fazer a comparação
      this.compareData();
      this.comparingDataLoading = false;
      this.cdr.detectChanges();
    });
  }

  // Função para comparar os dados e aplicar classes de comparação
  async compareData() {
    if (!this.mainData || !this.comparingData) {
      return;
    }

    try {
      const processedData: any[] = [];

      // Processar itens da tabela de comparação (direita)
      this.comparingData.forEach((comparingItem) => {
        const referenceItem = this.mainData.find(
          (ref) => ref.id === comparingItem.id
        );

        if (referenceItem) {
          // Item existe em ambas as tabelas - verificar se há diferenças
          const changedFields: any = {};
          let hasChanges = false;

          // Comparar campos específicos
          if (referenceItem.file !== comparingItem.file) {
            changedFields.file = true;
            hasChanges = true;
          }
          if (referenceItem.applicable !== comparingItem.applicable) {
            changedFields.applicable = true;
            hasChanges = true;
          }
          if (referenceItem.date_updated !== comparingItem.date_updated) {
            changedFields.date_updated = true;
            hasChanges = true;
          }
          if (referenceItem.fund !== comparingItem.fund) {
            changedFields.fund = true;
            hasChanges = true;
          }
          if (referenceItem.content !== comparingItem.content) {
            changedFields.content = true;
            hasChanges = true;
          }

          processedData.push({
            ...comparingItem,
            compareClass: hasChanges ? 'row-compare-modified' : '',
            changedFields: hasChanges ? changedFields : {},
          });
        } else {
          // Item novo (existe só na direita)
          processedData.push({
            ...comparingItem,
            compareClass: 'row-compare-added',
            changedFields: {},
          });
        }
      });

      // Adicionar itens removidos (existem só na esquerda)
      this.mainData.forEach((referenceItem) => {
        const existsInComparing = this.comparingData.find(
          (comp) => comp.id === referenceItem.id
        );

        if (!existsInComparing) {
          // Item removido - adicionar na lista de comparação com classe removed
          processedData.push({
            ...referenceItem,
            compareClass: 'row-compare-removed',
            changedFields: {},
          });
        }
      });

      this.comparingData = processedData;
      this.comparingDataLoading = false;
      this.comparingLoading = false;
      this.cdr.detectChanges();
    } catch (error) {
      this.comparingDataLoading = false;
      this.comparingLoading = false;
    }
  }

  async getApprovalData(versionType: 'main' | 'comparing' | null = null) {
    // Não chamar APIs v2 - apenas verificar cache
    // Se não houver dados, não fazer nada e não mostrar loading
    if (versionType === 'main') {
      this.mainData = [];
      this.mainLoading = false;
      this.cdr.detectChanges();
    } else if (versionType === 'comparing') {
      this.comparingData = [];
      this.comparingLoading = false;
      this.cdr.detectChanges();
    } else {
      this.data = [];
      this.loading = false;
    }
    return;
    
    /* Código antigo comentado - não chamar APIs v2
    try {
      const req_params: any = {
        register: this.route.snapshot.params.id,
        tab: 'draft',
      };

      if (
        versionType === 'main' &&
        this.approvalService.approvalData.mainVersion
      ) {
        req_params['version'] = this.approvalService.approvalData.mainVersion;
      } else if (
        versionType === 'comparing' &&
        this.approvalService.approvalData.comparingVersion
      ) {
        req_params['version'] =
          this.approvalService.approvalData.comparingVersion;
      }

      // Se estiver em modo de comparação e não houver versão selecionada, não fazer requisição
      if (
        this.comparingVersions &&
        versionType &&
        ((versionType === 'main' &&
          !this.approvalService.approvalData.mainVersion) ||
          (versionType === 'comparing' &&
            !this.approvalService.approvalData.comparingVersion))
      ) {
        if (versionType === 'main') {
          this.mainData = [];
          this.mainLoading = false;
          this.cdr.detectChanges();
        } else if (versionType === 'comparing') {
          this.comparingData = [];
          this.comparingLoading = false;
          this.cdr.detectChanges();
        }
        return;
      }

      // Se estiver em modo de comparação mas não tiver parâmetros de versão, não fazer requisição
      if (this.comparingVersions && versionType && !req_params['version']) {
        if (versionType === 'main') {
          this.mainData = [];
          this.mainLoading = false;
          this.cdr.detectChanges();
        } else if (versionType === 'comparing') {
          this.comparingData = [];
          this.comparingLoading = false;
          this.cdr.detectChanges();
        }
        return;
      }
      const data = await this.api.get<any[]>({
        route: `api/v2/approvals/version/`,
        token: true,
        params: req_params,
      });

      this.userId = data[0].id;
      this.generateDocumentation();

      if (versionType) {
        this.getData(versionType);
      } else {
        this.getData();
      }

      this.role = data[0].register.role?.applicable ?? null;
      this.haveCoobligation = data[0].assignor
        ? data[0].assignor.joint_obligation
          ? true
          : false
        : false;
      this.funds = data[0].links
        ? data[0].links.map((item) => {
            return {
              value: item.fund.id,
              label: item.fund.name,
            };
          })
        : [];

      if (versionType) {
        if (versionType === 'main') {
          this.mainLoading = false;
        } else if (versionType === 'comparing') {
          this.comparingLoading = false;
        }
        this.cdr.detectChanges();
      } else {
        this.loading = false;
      }
    } catch (error) {
      if (versionType) {
        if (versionType === 'main') {
          this.mainLoading = false;
        } else if (versionType === 'comparing') {
          this.comparingLoading = false;
        }
        this.cdr.detectChanges();
      } else {
        this.loading = false;
      }
    }
    */
  }

  async createTemplate() {
    let documentName = '';
    let coobligation = '';
    let fund = this.funds.find(
      (item) => item.value === this.form.controls.fund.value
    ).label;
    let res: any;

    if (this.role === 'assignor') {
      if (this.haveCoobligation) {
        coobligation = 'sem coobrigação';
      } else {
        coobligation = 'com coobrigação';
      }

      documentName = `Minuta de contrato ${coobligation} - ${fund}`;
    }

    if (this.role === 'shareholder') {
      documentName = `Termo de Adesão - ${fund}`;
    }

    const payload = {
      name: documentName,
      codiname: '',
      content: this.form.controls.content.value,
      coobligation:
        this.role === 'assignor' && this.haveCoobligation ? true : false,
      user: this.userId,
      fund: this.form.controls.fund.value,
      applicable: this.role,
    };

    try {
      if (this.selectedDraft) {
        res = await this.api.patch({
          route: `api/draft/${this.selectedDraft}/`,
          token: true,
          body: payload,
        });

        const findIdx = this.data.findIndex(
          (item) => item.id === this.selectedDraft
        );

        this.data[findIdx] = {
          file: res.name,
          applicable: res.applicable,
          date_updated: res.updated_at,
          id: res.id,
          fund: res.fund,
          content: res.content,
        };
      } else {
        res = await this.api.post({
          route: `api/draft/`,
          token: true,
          body: payload,
        });

        this.data.push({
          file: res.name,
          applicable: res.applicable,
          date_updated: res.updated_at,
          id: res.id,
          fund: res.fund,
          content: res.content,
        });
      }

      this.toast.show(
        'info',
        'Sucesso',
        this.selectedDraft
          ? 'Template atualizado, já está disponível para ser utilizado pelo sistema'
          : 'Um novo template foi criado e já está disponível para ser utilizado pelo sistema'
      );

      this.closeModal();
    } catch (error) {
    }
  }

  async getData(versionType: 'main' | 'comparing' | null = null) {
    const res = await this.api.get({
      route: `api/draft`,
      token: true,
    });

    const processedData = res.data
      .filter((item) => item.user === this.userId)
      .map((item: any) => {
        return {
          file: item.name,
          applicable: item.applicable,
          date_updated: item.updated_at,
          id: item.id,
          fund: item.fund,
          content: item.content,
        };
      });

    if (versionType === 'main') {
      this.mainData = processedData;
    } else if (versionType === 'comparing') {
      this.comparingData = processedData;
    } else {
      this.data = processedData;
    }
  }

  async handleDelete(id: any) {
    if (this.findItem(id)) {
      try {
        const res = await this.api.delete({
          route: `api/draft/${this.document.id}/`,
          token: true,
        });

        this.data = this.data.filter(
          (item: any) => Number(item.id) !== Number(this.document.id)
        );

        this.document = null;
      } catch (error) {
      }
    }
  }

  handleEdit(id: any) {
    if (this.findItem(id)) {
      this.selectedDraft = id;

      this.form.patchValue({
        content: this.document.content,
        fund: this.document.fund,
      });
      this.showModal = true;
    }
  }

  async handleOpenVisualizer(id: any) {
    if (this.findItem(id)) {
      window
        .open(
          `${this.api.url}/api/draft/${this.document.id}/?pdf=true`,
          '_blank'
        )
        .focus();
    }
  }

  findItem(id: any) {
    const finded = this.data.find((item: any) => {
      if (item.id) {
        return Number(item.id) === Number(id);
      }

      return false;
    });

    if (finded) {
      this.document = finded;
    }

    return finded;
  }

  formatApplicable(applicable: string) {
    switch (applicable) {
      case 'assignor':
        return 'Cedente';
      case 'shareholder':
        return 'Cotista';
      case 'provider':
        return 'Prestador de serviço';
      default:
        return 'Cedente';
    }
  }

  formatDate(date: string) {
    const day = date.split('T').shift()?.split('-').reverse().join('/');
    const hours = date.split('T').pop()?.slice(0, 5);

    return `${day} às ${hours}`;
  }

  generateEndereco(tipo) {
    return {
      logradouro: `Logradouro do ${tipo}`,
      numero: `Número do ${tipo}`,
      complemento: `Complemento do ${tipo}`,
      bairro: `Bairro do ${tipo}`,
      cidade: `Cidade do ${tipo}`,
      estado: `Estado do ${tipo}`,
      cep: `CEP do ${tipo}`,
    };
  }

  generateUsuario(tipo) {
    return {
      nome: `Nome completo do ${tipo}`,
      documento: `Número do documento do ${tipo}`,
      endereco: this.generateEndereco(tipo),
    };
  }

  generateData() {
    return {
      dia: `Dia`,
      mes: `Mês`,
      ano: `Ano`,
      hora: `Hora`,
      minutos: `Minutos`,
    };
  }

  generateFundo(tipo) {
    return {
      nome: `Nome do ${tipo}`,
      documento: `Número do documento do ${tipo}`,
    };
  }

  generateBank(tipo) {
    return {
      banco: `Nome do banco do ${tipo}`,
      agencia: `Número da agência do ${tipo}`,
      conta: `Número da conta do ${tipo}`,
    };
  }

  generateDocumentationArray(obj, prefix) {
    const result = [];
    Object.keys(obj).forEach((key) => {
      if (typeof obj[key] === 'object') {
        result.push(
          ...this.generateDocumentationArray(obj[key], `${prefix}${key}.`)
        );
      } else {
        result.push(`{{${prefix}${key}}}: ${obj[key]}`);
      }
    });
    return result;
  }

  generatePrimaryArray = (obj) => {
    const array = [];

    Object.keys(obj).forEach((key) => {
      array.push({
        name: key,
        children: this.generateDocumentationArray(obj[key], `${key}.`),
      });
    });

    return array;
  };

  generateDocumentation() {
    const documentation = {
      fundo: { ...this.generateFundo('Fundo'), ...this.generateBank('Fundo') },
      cedente: {
        ...this.generateUsuario('Cedente'),
        ...this.generateBank('Cedente'),
      },
      administrador: this.generateUsuario('Administrador'),
      gestor: this.generateUsuario('Gestor'),
      consultor: this.generateUsuario('Consultor'),
      data: this.generateData(),
    };

    this.documentation = this.generatePrimaryArray(documentation);
  }

  formatFileName(file: string) {
    return file.split('/').pop()?.split('.').shift();
  }

  closeModal() {
    this.showModal = false;
    this.selectedDraft = null;
    this.form.reset();
  }

  triggerModal() {
    this.showModal = true;
  }

  getDisabled() {
    return !this.form.valid;
  }

  // Funções auxiliares para verificar campos alterados
  isFieldChanged(item: any, field: string): boolean {
    if (!item || !item.changedFields) return false;
    return item.changedFields[field] === true;
  }
}
